import React from 'react';

import { InternationalPhoneCodeDto as Country } from '@api/ecommerce-xp';
import { CoreHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { ComposedElementProps, PopupMenuItem } from '@bcp-nextgen-dx-component-factory/design-system';
import { useLogger } from '@hooks';
import { useServiceResponse } from '@store';

interface BuildPhoneCodesOutput {
	phoneCodes: PopupMenuItem[];
	initialPhoneId?: number;
	getSelectedPhone: (content: string) => Country;
	getSelectedPhoneId: (indicative: string) => number;
	deletePlus: (phone: string) => string;
}

interface BuildPhoneCodesInput {
	withFlags: boolean;
	initialCountry?: string;
}

export const useBuildPhoneCodes = (props: BuildPhoneCodesInput): BuildPhoneCodesOutput => {
	const { clickToCallAllInternationalDialingPhoneCodes: countries } = useServiceResponse();
	const logger = useLogger();
	const { withFlags, initialCountry = 'PRT' } = props;

	const deletePlus = (phone: string) => {
		if (!CoreHelper.isNullOrEmpty(phone) && phone?.[0] === '+') {
			return phone?.substring(1);
		} else {
			return phone as string;
		}
	};

	const phoneCodes = React.useMemo(() => {
		const indicatives = !CoreHelper.isNullOrEmpty(countries?.internationalPhoneCodes)
			? countries?.internationalPhoneCodes
			: [];
		const result: PopupMenuItem[] | undefined = indicatives?.map((phoneCode) => {
			let contentIndicatives = !CoreHelper.isNullOrEmpty(phoneCode.internationalCode)
				? phoneCode.internationalCode?.[0] === '+'
					? (phoneCode.internationalCode as string)
					: `+${phoneCode.internationalCode as string}`
				: '';
			let flags: ComposedElementProps | undefined = undefined;
			if (withFlags) {
				contentIndicatives = !CoreHelper.isNullOrEmpty(phoneCode.internationalCode)
					? deletePlus(phoneCode.internationalCode as string)
					: '';
				flags = {
					icon: {
						source: phoneCode.flagImage,
					},
				};
			}
			return {
				leftIcon: flags,
				content: contentIndicatives,
			};
		});
		logger('useBuildPhoneCodes >', {
			retrieveCountries: countries,
			IsNullOrEmpty: CoreHelper.isNullOrEmpty(countries?.internationalPhoneCodes),
			indicatives: indicatives,
			result: result,
		});
		return result || []; // filter phonePrefix is empty
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getSelectedPhoneId = React.useCallback(
		(indicative: string) => {
			return phoneCodes.findIndex((phone) => {
				const initialIndicative = withFlags ? deletePlus(indicative) : `+${indicative}`;
				return phone.content === initialIndicative;
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[withFlags]
	);

	const initialPhone = countries?.internationalPhoneCodes?.filter((phone) => phone.country === initialCountry)[0];
	const initialPhoneId = getSelectedPhoneId(initialPhone?.internationalCode as string);

	const getSelectedPhone = React.useCallback(
		(phoneCodeSelected: string) => {
			let country: Country = {};
			if (!CoreHelper.isNullOrEmpty(countries?.internationalPhoneCodes)) {
				country = countries?.internationalPhoneCodes?.filter((phone) => {
					const indicative = withFlags
						? deletePlus(phone?.internationalCode as string)
						: `+${phone?.internationalCode}`;
					return phoneCodeSelected === indicative;
				})[0] as Country;
			}
			return country;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[withFlags]
	);

	return {
		phoneCodes,
		initialPhoneId,
		getSelectedPhone,
		getSelectedPhoneId,
		deletePlus,
	};
};
